import $ from 'jquery';
import api, {isValidationError, getValidationErrors} from '../../../general/js/api';
import gtm from '../../../general/js/gtm';
import createEvent from '../../../general/js/create-event';
import Parsley from 'parsleyjs';
import ParsleyLocale from "./parsley-locale";
import errorsSummaryTemplate from '../html/errors-summary.hbs';
import BaseComponent from '../../../general/js/base-component';
import {EVENT_AFTER_SUBMIT, EVENT_ON_ERROR, DEFAULT_RECAPTCHA_FIELD_NAME} from '../../recaptcha/js/recaptcha.component';

const ENCTYPE_URLENCODED = 'application/x-www-form-urlencoded';
const ENCTYPE_MULTIPART = 'multipart/form-data';

export const EVENT_FIELD_VALIDATION_FAILED = 'field-validation-failed';
export const EVENT_FIELD_VALIDATION_SUCCEEDED = 'field-validation-succeeded';

export default class AsyncFormComponent extends BaseComponent {

    constructor() {
        super(...arguments)
        this.$element = $(this.element);
        this.method = this.element.method || 'get';
        this.action = this.element.action;
        this.enctype = this.element.enctype || ENCTYPE_URLENCODED;

        if (this.element.nodeName !== 'FORM') {
            throw new Error('element must be a form');
        }

        if (this.props.onSuccess) {
            if (typeof this.props.onSuccess === "function") {
                this.onSuccess = this.props.onSuccess;
            } else {
                throw new Error('onSuccess must be function');
            }
        }

        this.isSubmiting = false;

        this.formControlSelector = '.js-form-control';
        this.serverInvalidFields = {};

        //reset form in case of success submit
        this.resetOnSuccess = ('resetOnSuccess' in this.element.dataset) ? this.element.dataset.resetOnSuccess !== 'false' : true;

        this.spinnerElement = this.element.querySelector('.js-async-form-spinner');
        this.errorsSummary = this.element.querySelector('.js-async-form-errors-summary');
        this.submitElement = this.element.querySelector('.js-async-form-submit');
        this.init();
    }

    init() {
        this.parsleyForm = new Parsley.Factory(this.element, {
            inputs: Parsley.options.inputs + ',[data-parsley-subforms-required]',
            // exclude hidden and disabled
            // but also we are able manage it by adding particular attribute to fields' parent and to make fields to validate despite of their visibility
            excluded: (index, element) => {
                let $element = $(element);

                let defaultExclude = $element.is('input[type=button], input[type=submit], input[type=reset], input[type=hidden], :disabled');
                if (defaultExclude) {
                    return true;
                }

                let parentGroup = $element.closest('[data-hidden-group]');
                return parentGroup.length > 0 ? parentGroup.is('[data-hidden-group="is-hidden"]') : $element.is(':hidden');
            },
            errorClass: 'has-error',
            successClass: 'is-success',
            classHandler: (field) => {
                return field.$element.closest(this.formControlSelector);
            },
            errorsContainer: (field) => {
                return field.$element.closest(this.formControlSelector);
            },
            errorsWrapper: '<div class="error-message"></ul>',
            errorTemplate: '<div></div>'
        });

        //prevent form submit
        this.element.addEventListener('submit', e => e.preventDefault());
        this.parsleyForm.on('form:validate', this.onSubmitClick.bind(this));
        this.parsleyForm.on('field:error', this.onFieldValidationFail.bind(this));
        this.parsleyForm.on('field:success', this.onFieldValidationSuccess.bind(this));
        this.parsleyForm.on('form:submit', this.onSubmit.bind(this));
    }

    onSubmit(e) {
        if (!this.isSubmiting) {

            this.isSubmiting = true;
            try {
                this.beforeSubmit();
                api({
                    method: this.method,
                    url: this.getUrl(),
                    data: this.getRequestParams()
                }).then((result) => {
                    this.afterSubmit();
                    this.resetForm();
                    return this.handleSuccessSubmit(result.data);
                }, (error) => {
                    //first we have to proceed validation errors
                    if (isValidationError(error)) {
                        this.renderServerErrors(getValidationErrors(error));
                    }
                    this.afterSubmit();
                    return this.onError(error);
                });
            } catch (err) {
                console.error(err);
            }
        }
    }

    handleSuccessSubmit(result) {
        if (result.submitTrackingInfo) {
            gtm.push(result.submitTrackingInfo);
        }
        return this.onSuccess(result);
    }

    renderServerErrors(errors) {
        let groupedErrors = {};

        errors.forEach((error, i) => {
            if (!(error.key in groupedErrors)) {
                groupedErrors[error.key] = [];
            }
            groupedErrors[error.key].push(error.message);
        });

        let hasFieldErrors = false;

        this.parsleyForm.fields.forEach((field) => {
            let element = field.$element[0];
            // for the cases when we emulate field with non HTMLInputElement and so on
            // for example for subformsRequired validator
            let fieldName = element.name ? element.name : element.getAttribute('data-name');

            if (fieldName in groupedErrors) {

                hasFieldErrors = true;

                //programmatically add constraint once (not public Parsley method)
                if (!(fieldName in this.serverInvalidFields)) {
                    field.addConstraint('server');
                    this.serverInvalidFields.fieldName = true;
                }

                let errors = groupedErrors[fieldName];
                delete groupedErrors[fieldName];

                //programmatically set constraint error text
                field.options.serverMessage = errors.join(', ');
                //refresh stored value
                delete field.serverErrorValue;

                if (AsyncFormComponent.isReturnsEmpty(field)) {
                    field.options.validateIfEmpty = true;
                }

                if (AsyncFormComponent.isCaptchaField(field)) {
                    field.element.dispatchEvent(createEvent(EVENT_ON_ERROR));
                }
            }
        });

        //validate to cause an error if we have field errors
        if (hasFieldErrors) {
            this.parsleyForm.validate();
        }

        this.showErrorsSummary(groupedErrors);
    }

    static isCaptchaField(field) {
        return 'recaptcha' in field.constraintsByName;
    }

    showErrorsSummary(groupped) {
        if (this.errorsSummary) {
            if (Object.keys(groupped).length) {
                let errors = Object.keys(groupped).reduce((errors, key) => [...errors, ...groupped[key]], []);
                this.errorsSummary.innerHTML = errorsSummaryTemplate({errors});
                this.errorsSummary.classList.add('is-shown');
            } else {
                this.hideErrorsSummary();
            }
        }
    }

    hideErrorsSummary() {
        if (this.errorsSummary) {
            this.errorsSummary.classList.remove('is-shown');
        }
    }

    static isReturnsEmpty(field) {
        return field.element.tagName === 'INPUT' && (field.element.type === 'file');
    }

    getRequestParams() {
        let data = null;
        switch (this.enctype) {
            case ENCTYPE_URLENCODED:
                data = this.getUrlencodedFormData();
                break;
            case ENCTYPE_MULTIPART:
                data = this.getMultipartFormData();
                break;
            default:
                throw new Error('Unknown form enctype');
        }

        return data;
    }

    getUrlencodedFormData() {
        let dataObject = this.$element.serializeJSON({
            checkboxUncheckedValue: "false",
            parseBooleans: true,
            useIntKeysAsArrayIndex: true
        });
        // we don't need default captcha entry in our data
        // because we handle it manually in RecaptchaComponent
        delete dataObject[DEFAULT_RECAPTCHA_FIELD_NAME];
        return $.param(dataObject);
    }

    getMultipartFormData() {

        let recaptureField = this.element.querySelector("[name='" + DEFAULT_RECAPTCHA_FIELD_NAME + "']");
        // Removing the name of the recaptcha field.
        // It shouldn't be included to FormData object.
        if(recaptureField) recaptureField.removeAttribute("name");

        let formData = new FormData(this.element);
        // Restoring the name of the recaptcha field.
        if(recaptureField) recaptureField.setAttribute("name", DEFAULT_RECAPTCHA_FIELD_NAME);

        let uncheckedCheckboxesData = this.getUncheckedCheckboxesData();
        // patch default form data with unchecked checkboxes
        Object.keys(uncheckedCheckboxesData).map((name) => {
            formData.append(name, uncheckedCheckboxesData[name]);
        });
        return formData;
    }

    getUncheckedCheckboxesData() {
        let checkboxesInputs = [...this.element.querySelectorAll('input[type="checkbox"]')];
        return checkboxesInputs.reduce((result, checkbox) => {
            if (!checkbox.checked) {
                result[checkbox.name] = false;
            }
            return result;
        }, {});
    }

    getUrl() {
        return this.action;
    }

    onSubmitClick(){
        //hook for descendants
    }

    onFieldValidationFail(field) {
        let event = createEvent(EVENT_FIELD_VALIDATION_FAILED);
        event.field = field;
        field.element.dispatchEvent(event);
    }

    onFieldValidationSuccess(field) {
        let event = createEvent(EVENT_FIELD_VALIDATION_SUCCEEDED);
        event.field = field;
        field.element.dispatchEvent(event);
    }

    onBeforeSubmit() {
        //hook for descendants
        //can be used for showing preloader
    }

    onSuccess(result) {
        //hook for descendants
    }

    onError(error) {
        //hook for descendants
    }

    onAfterSubmit() {
        //hook for descendants
        //can be used for hiding preloader
    }

    onAfterReset(){
        //hook for descendants
    }

    afterSubmit() {
        if (this.spinnerElement) {
            this.spinnerElement.classList.remove('is-loading');
        }
        if (this.submitElement) {
            this.submitElement.disabled = false;
        }
        this.handleCaptchaInputs();
        this.isSubmiting = false;
        this.onAfterSubmit();
    }

    beforeSubmit() {
        if(this.spinnerElement){
            this.spinnerElement.classList.add('is-loading');
        }
        if (this.submitElement) {
            this.submitElement.disabled = true;
        }
        this.onBeforeSubmit();
    }

    resetForm() {
        if (this.resetOnSuccess) {
            //slow down a little so modal will be able to be hidden before reset
            setTimeout(() => {
                this.element.reset();
                this.parsleyForm.reset();
                this.onAfterReset();
            }, 1000);
        }

        this.hideErrorsSummary();
    }

    handleCaptchaInputs() {
        let captchas = this.parsleyForm.fields.filter(field => AsyncFormComponent.isCaptchaField(field));
        captchas.forEach(field => {
            //reset ui state of parsley for every captcha field
            field.reset();
            field.element.dispatchEvent(createEvent(EVENT_AFTER_SUBMIT))
        });
    }

    onDestroy() {
        this.parsleyForm.destroy();
    }
}

Parsley.addValidator('accountType', {
    validateMultiple: function (values) {
        return values.length > 0;
    },
    validateString: function (value) {
        return /\S/.test(value);
    },
    priority: 512,
    messages: {
        en: 'Please specify account type',
    }
});

Parsley.addValidator('subformsRequired', {
    validate: function (value, requirement, instance) {
        return instance.element.hasChildNodes();
    },
    priority: 512,
    messages: {
        en: 'Please add at least one entry',
    }
});


Parsley.addValidator('recaptcha', {
    validateString: function (value) {
        return /\S/.test(value);
    },
    priority: 512,
    messages: {
        en: 'Please confirm that you are not a robot',
    }
});

Parsley.addValidator('server', {
    validateString: function (value, c, field) {
        //if field cant store value we show error just once
        if (AsyncFormComponent.isReturnsEmpty(field)) {
            if (field.serverErrorValue !== true) {
                field.serverErrorValue = true;
                return false;
            } else {
                return true;
            }
        }

        //otherwise field is valid only if new value do not matches old one
        if ('serverErrorValue' in field) {
            return field.serverErrorValue !== field.$element.val()
        } else {
            field.serverErrorValue = field.$element.val();
            return false;
        }
    },
    requirementType: 'string',
    priority: 1024
});



// WEBPACK FOOTER //
// ./app/components/form/js/async-form.component.js